import React from "react";
import { buildQualifiedVideoPresentation } from "../platformAnalytics/qualifiedVideoPresentation";
import "./QualifiedVideoSummary.css";

/*
 * The campaign's video story: one headline, and the platforms that produced it.
 *
 * This component deliberately cannot render the total on its own. A
 * campaign-level video figure with no attribution is what misled before —
 * 185,728 "completions" looked homogeneous, so it was read as homogeneous —
 * and the qualified total would invite the same reading if it appeared as a
 * bare number. The rows are not supplementary detail; they are the evidence
 * that makes the headline safe to publish, so they ship together or not at all.
 *
 * All platform knowledge lives in the shared engine and its presentation
 * module. Nothing here branches on a platform key.
 */
export default function QualifiedVideoSummary({ qualifiedVideo, disabled = false }) {
  const presentation = buildQualifiedVideoPresentation(qualifiedVideo);
  if (!presentation) return null;

  return (
    <section
      className={`qvs-panel ${disabled ? "qvs-disabled" : ""}`}
      aria-label={presentation.label}
    >
      <div className="qvs-headline">
        <h2 className="qvs-headline-label">{presentation.label}</h2>
        <div className="qvs-headline-value">{presentation.totalText ?? "—"}</div>
        <p className="qvs-explainer">{presentation.explainer}</p>
        {presentation.incompleteNote ? (
          <p className="qvs-note">{presentation.incompleteNote}</p>
        ) : null}
      </div>

      <ul className="qvs-rows">
        {presentation.rows.map((row) => (
          <li className="qvs-row" key={row.platformKey}>
            <div className="qvs-row-count">
              <span className="qvs-row-label">{row.countLabel}</span>
              <span className="qvs-row-value">{row.countText}</span>
            </div>
            {/*
              A platform with no governed rate of its own shows none. Leaving
              the slot empty is the honest option: borrowing a neighbour's
              percentage to keep the rows visually even would restate the
              exact error the qualified metric exists to retire.
            */}
            {row.rateText ? (
              <div className="qvs-row-rate">
                <span className="qvs-row-label">{row.rateLabel}</span>
                <span className="qvs-row-value">{row.rateText}</span>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
